import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, ExternalLink, RefreshCw, MoreHorizontal, ArrowLeft } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useEbayListing } from '@/hooks/useEbayListing';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger
} from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import { useVisualViewportInset } from '@/hooks/useVisualViewportInset';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/lib/toast';
import { EbayDescriptionEditor } from './EbayDescriptionEditor';
import { useEbayDescription } from '@/hooks/useEbayDescription';
import { useEbayTemplates, useEbayCategoryResolution } from '@/hooks/useEbayTemplates';
import { goldGradientActive } from '@/lib/gradientStyles';
import { cn } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EbayTemplatesModal } from '@/components/ebay/EbayTemplatesModal';
import { composeDescription, isCardSimpComposerTemplate, type ComposerItem } from '@/lib/ebayDescriptionComposer';
import { composeEbayTitle, _TITLE_MAX } from '@/lib/ebayTitleComposer';
import { Pencil, RotateCcw } from 'lucide-react';
import { formatUsdCents } from '@/lib/formatMoney';
import { PRICE_INPUT_PROPS } from '@/lib/priceInputProps';

function escapeHtml(s: string) {
	return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function renderMustacheLite(tpl: string, vars: Record<string, string>): string {
	if (!tpl) return '';
	let out = tpl;
	// Loop so nested same-syntax blocks (e.g. {{#grade}} inside {{#grading_company}}) resolve too.
	for (let i = 0; i < 8; i++) {
		const next = out.replace(/\{\{#(\w+)\}\}([\s\S]*?)\{\{\/\1\}\}/g, (_m, k: string, inner: string) =>
			vars[k] && vars[k].trim() ? inner : ''
		);
		if (next === out) break;
		out = next;
	}
	out = out.replace(/\{\{(\w+)\}\}/g, (_m, k: string) => escapeHtml(vars[k] ?? ''));
	return out;
}
/**
 * Render a template's body. If it's the CardSimp composer sentinel, build fresh
 * type-aware HTML from the item data. Otherwise fall through to mustache-lite
 * with the variable map (user-authored templates).
 */
function renderTemplateBody(bodyHtml: string, item: ComposerItem | null, vars: Record<string, string>): string {
	if (isCardSimpComposerTemplate(bodyHtml) && item) {
		return composeDescription(item);
	}
	return renderMustacheLite(bodyHtml, vars);
}
function buildPreviewDoc(html: string) {
	return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{margin:12px;background:#fff;color:#1a1a1a;font:14px -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;}</style></head><body>${html}</body></html>`;
}


interface EbayListingDialogProps {
	itemId: string;
	hasPrice: boolean;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/**
	 * When true, the dialog saves listing prefs + description but does NOT publish to eBay.
	 * Used by Bulk List flow so the user can pre-configure many items, then list them all at once.
	 */
	deferList?: boolean;
}

/**
 * Controlled eBay listing settings dialog (quantity, OBO, description, list/end actions).
 * Lets parents trigger this from any UI surface (e.g. a DropdownMenuItem) instead of
 * being locked into the inline split-button shape.
 */
export function EbayListingDialog({ itemId, hasPrice, open, onOpenChange, deferList = false }: EbayListingDialogProps) {
	const { data: listing, list, end } = useEbayListing(itemId);
	const { stored: storedDesc, save: saveDesc } = useEbayDescription(itemId);
	const { list: templatesQuery } = useEbayTemplates();
	const category = useEbayCategoryResolution(itemId, open);

	const [oboEnabled, setOboEnabled] = useState(false);
	const [autoAccept, setAutoAccept] = useState('');
	const [autoDecline, setAutoDecline] = useState('');
	const [stockQty, setStockQty] = useState(1);
	const [listQty, setListQty] = useState('1');
	const [savingPrefs, setSavingPrefs] = useState(false);
	const [descHtml, setDescHtml] = useState('');
	const [descSource, setDescSource] = useState<'ai' | 'manual'>('manual');
	const [templateId, setTemplateId] = useState<string>('__none__');
	const [overrideCategoryId, setOverrideCategoryId] = useState<string | null>(null);
	const [showCategoryPicker, setShowCategoryPicker] = useState(false);
	const [missingAspects, setMissingAspects] = useState<Array<{ name: string; allowedValues?: string[] }>>([]);
	const [itemVars, setItemVars] = useState<Record<string, string>>({});
	const [composerItem, setComposerItem] = useState<ComposerItem | null>(null);
	const [templatesModalOpen, setTemplatesModalOpen] = useState(false);
	// Auction format state
	const [format, setFormat] = useState<'fixed_price' | 'auction'>('fixed_price');
	const [auctionStart, setAuctionStart] = useState('');
	const [auctionReserve, setAuctionReserve] = useState('');
	const [auctionBin, setAuctionBin] = useState('');
	const [auctionDuration, setAuctionDuration] = useState<1 | 3 | 5 | 7 | 10>(7);
	// Title override
	const [titleOverride, setTitleOverride] = useState<string>('');
	const [titleEditing, setTitleEditing] = useState(false);
	// eBay price (independent of CardSimp asking price — sellers pad this for eBay fees)
	const [askingPriceCents, setAskingPriceCents] = useState<number | null>(null);
	const [ebayPrice, setEbayPrice] = useState('');

	const handleTemplateChange = (newId: string) => {
		if (newId === '__manage__') {
			setTemplatesModalOpen(true);
			return;
		}
		setTemplateId(newId);
		if (newId === '__none__') return;
		const selected = (templatesQuery.data || []).find((t) => t.id === newId);
		if (!selected) return;
		const rendered = renderTemplateBody(selected.body_html || '', composerItem, itemVars);
		if (rendered.trim()) {
			setDescHtml(rendered);
			setDescSource('manual');
		}
	};

	useEffect(() => {
		if (!open || !itemId) return;
		(async () => {
			const { data } = await (supabase as any)
				.from('user_items')
				.select('ebay_best_offer_enabled,ebay_auto_accept_cents,ebay_auto_decline_cents,ebay_list_quantity,quantity,ebay_template_id,ebay_category_id,ebay_title_override,title,game,set_name,set_code,card_number,rarity,finish,finish_label,language,condition,slab_grade,grading_company,certification_number,description,is_first_edition,is_reverse_holo,sealed_condition,category,product_type,ebay_listing_format,ebay_auction_start_cents,ebay_auction_reserve_cents,ebay_auction_bin_cents,ebay_auction_duration_days,asking_price_cents,ebay_price_cents')
				.eq('id', itemId)
				.maybeSingle();
			if (data) {
				setOboEnabled(!!data.ebay_best_offer_enabled);
				setAutoAccept(data.ebay_auto_accept_cents ? (data.ebay_auto_accept_cents / 100).toString() : '');
				setAutoDecline(data.ebay_auto_decline_cents ? (data.ebay_auto_decline_cents / 100).toString() : '');
				setAskingPriceCents(data.asking_price_cents ?? null);
				const seedEbayPrice = data.ebay_price_cents ?? data.asking_price_cents ?? null;
				setEbayPrice(seedEbayPrice ? (seedEbayPrice / 100).toFixed(2) : '');
				const stock = Math.max(1, data.quantity ?? 1);
				setStockQty(stock);
				setListQty(String(data.ebay_list_quantity ?? stock));
				setTemplateId(data.ebay_template_id || '__none__');
				setOverrideCategoryId(data.ebay_category_id || null);
				const qty = data.quantity ?? 1;
				// Auction fields — default start to the item's asking price if none set yet.
				const savedFormat = (data.ebay_listing_format === 'auction' ? 'auction' : 'fixed_price') as 'fixed_price' | 'auction';
				setFormat(savedFormat);
				const seedStart = data.ebay_auction_start_cents ?? data.asking_price_cents ?? null;
				setAuctionStart(seedStart ? (seedStart / 100).toFixed(2) : '');
				setAuctionReserve(data.ebay_auction_reserve_cents ? (data.ebay_auction_reserve_cents / 100).toFixed(2) : '');
				setAuctionBin(data.ebay_auction_bin_cents ? (data.ebay_auction_bin_cents / 100).toFixed(2) : '');
				setAuctionDuration(([1, 3, 5, 7, 10].includes(data.ebay_auction_duration_days) ? data.ebay_auction_duration_days : 7) as 1 | 3 | 5 | 7 | 10);
				// Raw item snapshot for the type-aware composer (CardSimp default).
				setComposerItem({
					title: data.title,
					game: data.game,
					set_name: data.set_name,
					set_code: data.set_code,
					card_number: data.card_number,
					rarity: data.rarity,
					finish: data.finish,
					language: data.language,
					condition: data.condition,
					slab_grade: data.slab_grade,
					grading_company: data.grading_company,
					certification_number: data.certification_number,
					description: data.description,
					is_first_edition: data.is_first_edition,
					is_reverse_holo: data.is_reverse_holo,
					sealed_condition: data.sealed_condition,
					category: data.category,
					product_type: data.product_type,
					quantity: qty,
					finish_label: data.finish_label,
					ebay_title_override: data.ebay_title_override,
				} as any);
				setTitleOverride(data.ebay_title_override || '');
				setTitleEditing(false);
				// Mustache vars retained for user-authored templates.
				setItemVars({
					title: data.title || '',
					game: data.game || '',
					set: data.set_name || data.set_code || '',
					set_name: data.set_name || '',
					set_code: data.set_code || '',
					card_number: data.card_number || '',
					rarity: data.rarity || '',
					finish: data.finish || '',
					language: data.language || '',
					condition: data.condition || '',
					grade: data.slab_grade ? String(data.slab_grade) : '',
					grading_company: data.grading_company || '',
					cert_number: data.certification_number || '',
					quantity: String(qty),
					edition: data.is_first_edition ? '1st Edition' : '',
					variant: data.is_reverse_holo ? 'Reverse Holo' : '',
					sealed_condition: data.sealed_condition || '',
					category: data.category || '',
					product_type: data.product_type || '',
					seller_note: data.description || '',
					seller_handle: '',
				});
			}
			setMissingAspects([]);
		})();
	}, [open, itemId]);


	useEffect(() => {
		if (!open) return;
		if (storedDesc.data?.ebay_description_html != null) {
			setDescHtml(storedDesc.data.ebay_description_html);
			setDescSource((storedDesc.data.ebay_description_source as any) || 'manual');
		} else {
			setDescHtml('');
			setDescSource('manual');
		}
	}, [open, storedDesc.data?.ebay_description_html, storedDesc.data?.ebay_description_source]);

	const isLive = listing?.status === 'active' && !!listing.listing_id;

	const handleListClick = async () => {
		setSavingPrefs(true);
		const accCents = autoAccept ? Math.round(parseFloat(autoAccept) * 100) : null;
		const decCents = autoDecline ? Math.round(parseFloat(autoDecline) * 100) : null;
		const parsedQty = parseInt(listQty, 10);
		const finalQty = Number.isFinite(parsedQty) ? Math.max(1, Math.min(stockQty, parsedQty)) : stockQty;
		const startCents = auctionStart ? Math.round(parseFloat(auctionStart) * 100) : null;
		const reserveCents = auctionReserve ? Math.round(parseFloat(auctionReserve) * 100) : null;
		const binCents = auctionBin ? Math.round(parseFloat(auctionBin) * 100) : null;
		if (format === 'auction') {
			if (!startCents || startCents <= 0) {
				setSavingPrefs(false);
				toast.error('Set an auction start price.');
				return;
			}
			if (binCents && startCents && binCents < startCents) {
				setSavingPrefs(false);
				toast.error('Buy It Now must be at or above the start price.');
				return;
			}
		}
		const { error } = await (supabase as any)
			.from('user_items')
			.update({
				ebay_best_offer_enabled: format === 'auction' ? false : oboEnabled,
				ebay_auto_accept_cents: format === 'fixed_price' && oboEnabled ? accCents : null,
				ebay_auto_decline_cents: format === 'fixed_price' && oboEnabled ? decCents : null,
				ebay_list_quantity: format === 'auction' ? 1 : finalQty,
				ebay_template_id: templateId && templateId !== '__none__' ? templateId : null,
				ebay_category_id: overrideCategoryId || null,
				ebay_listing_format: format,
				ebay_auction_start_cents: format === 'auction' ? startCents : null,
				ebay_auction_reserve_cents: format === 'auction' ? reserveCents : null,
				ebay_auction_bin_cents: format === 'auction' ? binCents : null,
				ebay_auction_duration_days: format === 'auction' ? auctionDuration : null,
				ebay_title_override: titleOverride.trim() ? titleOverride.trim().slice(0, _TITLE_MAX) : null,
				ebay_price_cents: format === 'fixed_price' && ebayPrice.trim()
					? Math.max(1, Math.round(parseFloat(ebayPrice) * 100))
					: null
			})
			.eq('id', itemId);
		if (error) {
			setSavingPrefs(false);
			toast.error(`Could not save listing settings: ${error.message}`);
			return;
		}
		try {
			await saveDesc.mutateAsync({ html: descHtml, source: descSource });
		} catch (err: any) {
			setSavingPrefs(false);
			toast.error(`Could not save description: ${err?.message || err}`);
			return;
		}
		setSavingPrefs(false);
		setMissingAspects([]);
		if (deferList) {
			toast.success('Settings saved — will list with the batch');
			onOpenChange(false);
			return;
		}
		try {
			await list.mutateAsync({ format });
			onOpenChange(false);
		} catch (err: any) {
			if (Array.isArray(err?.missing) && err.missing.length > 0) {
				setMissingAspects(
					err.missing.map((m: unknown) => {
						if (typeof m === 'string') return { name: m };
						if (m && typeof m === 'object') {
							const obj = m as { name?: unknown; allowedValues?: unknown };
							const name = typeof obj.name === 'string' ? obj.name : String(obj.name ?? '');
							const allowedValues = Array.isArray(obj.allowedValues)
								? obj.allowedValues.filter((v): v is string => typeof v === 'string')
								: undefined;
							return { name, allowedValues };
						}
						return { name: String(m) };
					}).filter((a) => a.name.length > 0),
				);
			}
		}
	};

	const isMobile = useIsMobile();
	const { keyboardHeight: kbInset } = useVisualViewportInset();
	const titleText = deferList ? 'Listing settings' : isLive ? 'Manage eBay Listing' : 'List on eBay';

	const scrollInner = (
		<>


					{!isLive && (
						<div className="space-y-1.5">
							<Label className="text-sm font-medium">Format</Label>
							<div className="grid grid-cols-2 gap-1 p-1 rounded-full bg-white/[0.05] border border-white/[0.08]">
								<button
									type="button"
									onClick={() => setFormat('fixed_price')}
									className={cn('h-8 rounded-full text-xs font-medium transition', format === 'fixed_price' ? goldGradientActive : 'text-muted-foreground hover:text-foreground')}
								>Fixed price</button>
								<button
									type="button"
									onClick={() => setFormat('auction')}
									className={cn('h-8 rounded-full text-xs font-medium transition', format === 'auction' ? goldGradientActive : 'text-muted-foreground hover:text-foreground')}
								>Auction</button>
							</div>
						</div>
					)}

					{format === 'fixed_price' && (
						<>
							<div className="space-y-1">
								<Label htmlFor="ebay-price" className="text-sm font-medium">eBay list price</Label>
								<div className="relative">
									<span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
									<Input
										id="ebay-price"
										type="text"
										inputMode="decimal"
										pattern="[0-9]*\.?[0-9]*"
										{...PRICE_INPUT_PROPS}
										value={ebayPrice}
										onChange={(e) => setEbayPrice(e.target.value.replace(/[^0-9.]/g, ''))}
										placeholder={askingPriceCents ? (askingPriceCents / 100).toFixed(2) : '0.00'}
										className="pl-7"
									/>
								</div>
								<p className="text-xs text-muted-foreground">
									{askingPriceCents != null ? (
										<>CardSimp asking price: <span className="text-foreground/80">{formatUsdCents(askingPriceCents)}</span>. Pad this to cover eBay fees — it won't change your CardSimp price.</>
									) : (
										<>Independent of your CardSimp asking price. Pad this to cover eBay fees.</>
									)}
								</p>
							</div>
							<div className="space-y-1">
								<Label htmlFor="list-qty" className="text-sm font-medium">Quantity to list</Label>
								<Input id="list-qty" type="number" min={1} max={stockQty} value={listQty} onChange={(e) => setListQty(e.target.value)} />
								<p className="text-xs text-muted-foreground">You have {stockQty} in stock</p>
							</div>
							<div className="flex items-center justify-between">
								<div>
									<Label htmlFor="obo-toggle" className="text-sm font-medium">Best Offer (OBO)</Label>
									<p className="text-xs text-muted-foreground">Allow buyers to send offers</p>
								</div>
								<Switch id="obo-toggle" checked={oboEnabled} onCheckedChange={setOboEnabled} />
							</div>
							{oboEnabled && (
								<div className="space-y-3">
									<div className="space-y-1">
										<Label htmlFor="auto-accept" className="text-xs">Auto-accept at or above ($)</Label>
										<div className="relative">
											<span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
											<Input id="auto-accept" type="text" inputMode="decimal" pattern="[0-9]*\.?[0-9]*" {...PRICE_INPUT_PROPS} placeholder="optional" value={autoAccept} onChange={(e) => setAutoAccept(e.target.value.replace(/[^0-9.]/g, ''))} className="pl-7" />
										</div>
									</div>
									<div className="space-y-1">
										<Label htmlFor="auto-decline" className="text-xs">Auto-decline below ($)</Label>
										<div className="relative">
											<span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
											<Input id="auto-decline" type="text" inputMode="decimal" pattern="[0-9]*\.?[0-9]*" {...PRICE_INPUT_PROPS} placeholder="optional" value={autoDecline} onChange={(e) => setAutoDecline(e.target.value.replace(/[^0-9.]/g, ''))} className="pl-7" />
										</div>
									</div>
								</div>
							)}
						</>
					)}

					{format === 'auction' && (
						<div className="space-y-3 rounded-lg border border-white/[0.08] bg-white/[0.02] p-3">
							<div className="space-y-1">
								<Label htmlFor="auc-start" className="text-xs">Start price ($) <span className="text-destructive">*</span></Label>
								<div className="relative">
									<span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
									<Input id="auc-start" type="text" inputMode="decimal" pattern="[0-9]*\.?[0-9]*" {...PRICE_INPUT_PROPS} value={auctionStart} onChange={(e) => setAuctionStart(e.target.value.replace(/[^0-9.]/g, ''))} className="pl-7" />
								</div>
							</div>
							{/* Reserve price hidden — left unset by default */}
							<div className="space-y-1">
								<Label htmlFor="auc-bin" className="text-xs">Buy It Now ($)</Label>
								<div className="relative">
									<span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
									<Input id="auc-bin" type="text" inputMode="decimal" pattern="[0-9]*\.?[0-9]*" {...PRICE_INPUT_PROPS} placeholder="optional — must be ≥ start price" value={auctionBin} onChange={(e) => setAuctionBin(e.target.value.replace(/[^0-9.]/g, ''))} className="pl-7" />
								</div>
							</div>
							<div className="space-y-1.5">
								<Label className="text-xs">Duration</Label>
								<div className="grid grid-cols-5 gap-1">
									{([1, 3, 5, 7, 10] as const).map((d) => (
										<button
											key={d}
											type="button"
											onClick={() => setAuctionDuration(d)}
											className={cn('h-8 rounded-full text-xs font-medium border transition', auctionDuration === d ? 'bg-active-gold/15 text-active-gold border-active-gold/40' : 'border-white/[0.08] text-muted-foreground hover:text-foreground')}
										>{d}d</button>
									))}
								</div>
							</div>
							<p className="text-[10px] text-muted-foreground leading-relaxed">
								Auctions list qty 1 and don't support Best Offer. Once bids come in, eBay locks most edits.
							</p>
						</div>
					)}
					{/* Category line + change */}
					<div className="border-t border-white/[0.08] pt-3 space-y-2">
						<Label className="text-sm font-medium">eBay category</Label>
						<div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
							<span className="truncate">
								{category.isLoading
									? 'Resolving best category…'
									: category.data
										? <>Listing in: <span className="text-foreground font-medium">{category.data.categoryName}</span> <span className="opacity-60">#{category.data.categoryId}</span></>
										: 'Default category'}
							</span>
							{category.data && category.data.suggestions.length > 1 && (
								<button
									type="button"
									className="text-xs text-active-gold hover:underline shrink-0"
									onClick={() => setShowCategoryPicker((v) => !v)}
								>
									{showCategoryPicker ? 'Done' : 'Change'}
								</button>
							)}
						</div>
						{showCategoryPicker && category.data && (
							<Select
								value={overrideCategoryId || category.data.categoryId}
								onValueChange={(v) => setOverrideCategoryId(v)}
							>
								<SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
								<SelectContent>
									{category.data.suggestions.map((s) => (
										<SelectItem key={s.categoryId} value={s.categoryId} className="text-xs">
											{s.categoryName} <span className="opacity-50 ml-1">#{s.categoryId}</span>
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						)}
					</div>


					{/* Title preview / override */}
					{composerItem && (() => {
						const auto = composeEbayTitle({ ...(composerItem as any), ebay_title_override: null });
						const shown = titleOverride.trim() ? titleOverride.slice(0, _TITLE_MAX) : auto;
						const remaining = _TITLE_MAX - shown.length;
						return (
							<div className="border-t border-white/[0.08] pt-3 space-y-2">
								<div className="flex items-center justify-between">
									<Label className="text-sm font-medium">eBay title</Label>
									{!titleEditing ? (
										<button
											type="button"
											onClick={() => { setTitleOverride(titleOverride || auto); setTitleEditing(true); }}
											className="inline-flex items-center gap-1 text-xs text-active-gold hover:underline"
										>
											<Pencil className="h-3 w-3" /> Edit
										</button>
									) : (
										<button
											type="button"
											onClick={() => { setTitleOverride(''); setTitleEditing(false); }}
											className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
										>
											<RotateCcw className="h-3 w-3" /> Reset to auto
										</button>
									)}
								</div>
								{!titleEditing ? (
									<div className="rounded-md border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-xs leading-relaxed text-foreground/90 break-words">
										{shown || <span className="text-muted-foreground italic">No title</span>}
									</div>
								) : (
									<>
										<Input
											value={titleOverride}
											maxLength={_TITLE_MAX}
											onChange={(e) => setTitleOverride(e.target.value.slice(0, _TITLE_MAX))}
											className="text-xs h-9"
										/>
										<div className="flex items-center justify-between text-[10px] text-muted-foreground">
											<span>{titleOverride.trim() ? 'Manual override' : 'Auto-composed'}</span>
											<span className={cn(remaining < 0 && 'text-destructive')}>{remaining} chars left</span>
										</div>
									</>
								)}
								<p className="text-[10px] text-muted-foreground">
									eBay caps titles at 80 characters. We pack the highest-signal keywords (set, number, grade, finish) automatically.
								</p>
							</div>
						);
					})()}

					{/* Template picker */}
					<div className="space-y-2">
						<div className="flex items-center justify-between">
							<Label className="text-sm font-medium">Description template</Label>
							<button type="button" onClick={() => setTemplatesModalOpen(true)} className="hidden sm:inline text-xs text-active-gold hover:underline">Manage</button>
						</div>

						<Select value={templateId} onValueChange={handleTemplateChange}>
							<SelectTrigger className="h-9 text-xs">
								<SelectValue placeholder="No template — use editor below" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="__none__" className="text-xs">No template — use editor below</SelectItem>
								{(templatesQuery.data || []).filter((t) => t.scope === 'preset').length > 0 && (
									<>
										<div className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground">CardSimp presets</div>
										{(templatesQuery.data || []).filter((t) => t.scope === 'preset').map((t) => (
											<SelectItem key={t.id} value={t.id} className="text-xs">{t.name}</SelectItem>
										))}
									</>
								)}
								{(templatesQuery.data || []).filter((t) => t.scope === 'user').length > 0 && (
									<>
										<div className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground">Your templates</div>
										{(templatesQuery.data || []).filter((t) => t.scope === 'user').map((t) => (
											<SelectItem key={t.id} value={t.id} className="text-xs">{t.name}</SelectItem>
										))}
									</>
								)}
								<div className="mt-1 border-t border-border/40 pt-1 sm:hidden">
									<SelectItem value="__manage__" className="text-xs text-active-gold focus:text-active-gold">Manage templates…</SelectItem>
								</div>
							</SelectContent>
						</Select>
						<p className="text-[10px] text-muted-foreground">
							Pick <strong className="text-foreground/80">CardSimp default</strong> for a safe baseline, or use one of your own templates.
						</p>

					</div>




					{missingAspects.length > 0 && (
						<div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning space-y-1">
							<div className="font-semibold">eBay requires these fields before this can publish:</div>
							<ul className="list-disc list-inside opacity-90 space-y-0.5">
								{missingAspects.map((a) => (
									<li key={a.name}>
										<span className="font-medium">{a.name}</span>
										{a.allowedValues && a.allowedValues.length > 0 && (
											<span className="opacity-75"> — e.g. {a.allowedValues.slice(0, 3).join(', ')}{a.allowedValues.length > 3 ? '…' : ''}</span>
										)}
									</li>
								))}
							</ul>
							<div className="text-[10px] opacity-80">Open Edit Item and fill them, then come back here.</div>
						</div>
					)}

					<div className="border-t border-white/[0.08] pt-3 space-y-2">
						<EbayDescriptionEditor
							itemId={itemId}
							html={descHtml}
							source={descSource}
							onChange={(h, s) => { setDescHtml(h); setDescSource(s); }}
						/>
						<p className="text-[10px] text-muted-foreground/80 leading-relaxed">
							Your description is your responsibility. CardSimp's default text only defines condition vocabulary — packaging, returns, and grading claims are up to you.
						</p>
					</div>

		</>
	);

	const footerInner = (
		<>
			<div className="flex items-center gap-2">

						{/* Primary CTA */}
						<Button
							type="button"
							className={cn(goldGradientActive, 'flex-1 h-10 rounded-full')}
							disabled={savingPrefs || list.isPending || !hasPrice}
							onClick={handleListClick}
						>
							{savingPrefs || list.isPending ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : deferList ? 'Save settings' : isLive ? 'Save & Update' : format === 'auction' ? 'Save & Start Auction' : 'Save & List on eBay'}
						</Button>

						{/* Overflow menu — only when live */}
						{isLive && (
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button
										type="button"
										variant="outline"
										size="icon"
										className="h-10 w-10 shrink-0 rounded-full bg-white/[0.05]"
										aria-label="More listing actions"
									>
										<MoreHorizontal className="h-4 w-4" />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end" side="top" className="w-56">
									<DropdownMenuItem onClick={() => window.open(`https://www.ebay.com/itm/${listing!.listing_id}`, '_blank')}>
										<ExternalLink className="h-4 w-4 mr-2" />
										View on eBay
									</DropdownMenuItem>
									<DropdownMenuItem
										onClick={async () => {
											const t = toast.loading('Checking eBay for sales...');
											const { data, error } = await supabase.functions.invoke('ebay-sync-orders', { body: {} });
											toast.dismiss(t);
											if (error) {
												toast.error(`Sync failed: ${error.message}`);
											} else {
												const created = data?.results?.[0]?.transactions_created ?? 0;
												toast.success(created > 0 ? `Recorded ${created} new eBay sale${created > 1 ? 's' : ''}` : 'No new sales found');
											}
										}}
									>
										<RefreshCw className="h-4 w-4 mr-2" />
										Check for sales
									</DropdownMenuItem>
									<DropdownMenuSeparator />
									<AlertDialog>
										<AlertDialogTrigger asChild>
											<DropdownMenuItem
												onSelect={(e) => e.preventDefault()}
												className="text-destructive focus:text-destructive"
												disabled={end.isPending}
											>
												{end.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <span className="w-4 mr-2" />}
												End listing
											</DropdownMenuItem>
										</AlertDialogTrigger>
										<AlertDialogContent>
											<AlertDialogHeader>
												<AlertDialogTitle>End this eBay listing?</AlertDialogTitle>
												<AlertDialogDescription>The listing will be withdrawn from eBay immediately. You can relist it later.</AlertDialogDescription>
											</AlertDialogHeader>
											<AlertDialogFooter>
												<AlertDialogCancel>Cancel</AlertDialogCancel>
												<AlertDialogAction onClick={() => { onOpenChange(false); end.mutate(); }}>End listing</AlertDialogAction>
											</AlertDialogFooter>
										</AlertDialogContent>
									</AlertDialog>
								</DropdownMenuContent>
							</DropdownMenu>
						)}
					</div>
					{!hasPrice && (
						<p className="mt-2 text-xs text-muted-foreground text-center">Set an asking price first to publish.</p>
					)}
		</>
	);


	if (isMobile) {
		return (
			<>
				<Sheet open={open} onOpenChange={onOpenChange}>
					<SheetContent
						side="bottom"
						grabbable={false}
						className="bg-background border-0 !p-0 !gap-0 flex flex-col h-[100dvh] max-h-[100dvh] rounded-none"
					>
						<SheetHeader
							className="px-4 shrink-0 text-left border-b border-white/[0.08] flex-row items-center gap-2 space-y-0"
							style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)', paddingBottom: '0.75rem' }}
						>
							<button
								type="button"
								onClick={() => onOpenChange(false)}
								aria-label="Close"
								className="-ml-2 h-10 w-10 inline-flex items-center justify-center rounded-full text-foreground/80 hover:text-foreground hover:bg-white/[0.06]"
							>
								<ArrowLeft className="w-5 h-5" />
							</button>
							<SheetTitle className="text-base font-semibold">{titleText}</SheetTitle>
						</SheetHeader>
						{deferList && (
							<p className="text-xs text-muted-foreground px-6 pt-3">Save these settings. We'll list this item with the rest of the batch.</p>
						)}
						<div className="space-y-4 px-6 py-4 overflow-y-auto flex-1 min-h-0">{scrollInner}</div>
						<div className="shrink-0 border-t border-white/[0.08] px-6 pt-3" style={{ paddingBottom: `calc(env(safe-area-inset-bottom) + 0.75rem + ${kbInset}px)` }}>{footerInner}</div>
					</SheetContent>
				</Sheet>
				<EbayTemplatesModal open={templatesModalOpen} onOpenChange={setTemplatesModalOpen} />
			</>
		);
	}

	return (
		<>
			<Dialog open={open} onOpenChange={onOpenChange}>
				<DialogContent className="sm:max-w-md p-0 gap-0 max-h-[85dvh] flex flex-col overflow-hidden">
					<DialogHeader className="shrink-0 px-6 pt-6 pb-3 border-b border-white/[0.08]">
						<DialogTitle>{titleText}</DialogTitle>
						{deferList && (
							<p className="text-xs text-muted-foreground pt-1">Save these settings. We'll list this item with the rest of the batch.</p>
						)}
					</DialogHeader>
					<div className="flex-1 min-h-0 overflow-y-auto space-y-4 px-6 py-4">{scrollInner}</div>
					<div className="shrink-0 border-t border-white/[0.08] px-6 py-3">{footerInner}</div>
				</DialogContent>
			</Dialog>
			<EbayTemplatesModal open={templatesModalOpen} onOpenChange={setTemplatesModalOpen} />
		</>
	);
}
